import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Brain, Send, Sparkles, TrendingUp, AlertCircle, CheckCircle, Zap, Target, DollarSign, Clock, Loader, BarChart3, Table as TableIcon, ChevronDown, ChevronUp, HardHat, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgenticAI = () => {
  const { t, i18n } = useTranslation();
  
  // Get initial welcome message based on language
  const getWelcomeMessage = (lang) => {
    if (lang === 'tr') {
      return 'Merhaba! Ben GPT-4o destekli **Proje Maliyet Kontrolü AI Asistanınızım**. Tüm proje verilerine gerçek zamanlı erişimim var:\n\n- **İş-402** (Fosfat 3 Faz 1) - $185.4M\n- **İş-403** (Master Gaz) - $211.9M\n\nSize şu konularda yardımcı olabilirim:\n• İlerleme analizi ve tahminleme\n• Maliyet sapma analizi\n• Risk değerlendirme ve azaltma\n• Kaynak optimizasyonu\n• Takvim kurtarma planlaması\n\nNe analiz etmek istersiniz?';
    }
    return 'Hello! I\'m your **Project Cost Control AI Assistant** powered by GPT-4o. I have real-time access to all project data including:\n\n- **İş-402** (Fosfat 3 Faz 1) - $185.4M\n- **İş-403** (Master Gaz) - $211.9M\n\nI can help you with:\n• Progress analysis & forecasting\n• Cost variance analysis\n• Risk assessment & mitigation\n• Resource optimization\n• Schedule recovery planning\n\nWhat would you like to analyze?';
  };

  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: getWelcomeMessage(i18n.language),
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Update welcome message when language changes
  useEffect(() => {
    setMessages([{
      type: 'ai',
      content: getWelcomeMessage(i18n.language),
      timestamp: new Date()
    }]);
  }, [i18n.language]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const parseAIResponse = (response) => {
    console.log('🔍 Parsing AI response (length):', response.length);
    
    let content = response;
    let chartData = null;
    let tableData = null;
    let chartType = null;

    // Find chart pattern: [CHART:type:[...]]
    // Using index-based search for more control
    const chartStart = response.indexOf('[CHART:');
    if (chartStart !== -1) {
      const typeEnd = response.indexOf(':', chartStart + 7);
      if (typeEnd !== -1) {
        chartType = response.substring(chartStart + 7, typeEnd).trim();
        console.log('📊 Found chart type:', chartType);
        
        // Find the JSON array start
        const jsonStart = typeEnd + 1;
        
        // Find matching closing bracket
        let bracketCount = 0;
        let jsonEnd = -1;
        for (let i = jsonStart; i < response.length; i++) {
          if (response[i] === '[') bracketCount++;
          else if (response[i] === ']') {
            bracketCount--;
            if (bracketCount === 0) {
              jsonEnd = i + 1;
              // Check if next char is also ']' (the closing of CHART marker)
              if (response[i + 1] === ']') {
                break;
              }
            }
          }
        }
        
        if (jsonEnd !== -1) {
          const jsonStr = response.substring(jsonStart, jsonEnd);
          console.log('📊 Raw chart JSON:', jsonStr);
          
          try {
            chartData = JSON.parse(jsonStr);
            console.log('✅ Parsed chart data:', chartData);
            
            // Remove the entire [CHART:..]] from content
            const chartEndMarker = jsonEnd + 1; // +1 for the final ]
            content = response.substring(0, chartStart) + response.substring(chartEndMarker);
          } catch (e) {
            console.error('❌ Failed to parse chart JSON:', e);
            console.log('Failed JSON string:', jsonStr);
          }
        }
      }
    } else {
      console.log('❌ No chart marker found');
    }

    // Find table pattern: [TABLE:{...}]
    const tableStart = content.indexOf('[TABLE:');
    if (tableStart !== -1) {
      const jsonStart = tableStart + 7;
      let braceCount = 0;
      let jsonEnd = -1;
      
      for (let i = jsonStart; i < content.length; i++) {
        if (content[i] === '{') braceCount++;
        else if (content[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
      
      if (jsonEnd !== -1) {
        const jsonStr = content.substring(jsonStart, jsonEnd);
        try {
          tableData = JSON.parse(jsonStr);
          console.log('✅ Parsed table data:', tableData);
          content = content.substring(0, tableStart) + content.substring(jsonEnd + 1);
        } catch (e) {
          console.error('❌ Failed to parse table:', e);
        }
      }
    }

    content = content.trim();
    console.log('📦 Final result:', { 
      hasChart: !!chartData, 
      hasTable: !!tableData, 
      chartType 
    });
    
    return { content, chartData, chartType, tableData };
  };

  const renderChart = (data, type) => {
    const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

    if (type === 'bar') {
      return (
        <div className="w-full h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    } else if (type === 'line') {
      return (
        <div className="w-full h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    } else if (type === 'pie') {
      return (
        <div className="w-full h-64 mt-3 flex justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }
    return null;
  };

  const renderTable = (data) => {
    if (!data || !data.headers || !data.rows) return null;

    return (
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              {data.headers.map((header, idx) => (
                <th key={idx} className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const parseContentSections = (content) => {
    // Split content by major sections (### or **Title:**)
    const sections = [];
    
    // Match patterns like "**Title:**" or "### Title"
    const sectionRegex = /(?:\*\*([^*]+):\*\*|###\s+([^\n]+))\n([\s\S]*?)(?=(?:\*\*[^*]+:\*\*|###\s+)|$)/g;
    let match;
    let lastIndex = 0;
    
    while ((match = sectionRegex.exec(content)) !== null) {
      const title = match[1] || match[2];
      const body = match[3].trim();
      
      sections.push({
        title: title.trim(),
        content: body
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // If no sections found, treat entire content as one section
    if (sections.length === 0) {
      // Check for any intro text before sections
      const lines = content.split('\n');
      let introText = '';
      let currentSection = null;
      
      lines.forEach(line => {
        if (line.match(/^\*\*[^*]+:\*\*/) || line.match(/^###/)) {
          if (currentSection) {
            sections.push(currentSection);
          }
          const title = line.replace(/^\*\*|\*\*:?$/g, '').replace(/^###\s*/, '').trim();
          currentSection = { title, content: '' };
        } else if (currentSection) {
          currentSection.content += line + '\n';
        } else {
          introText += line + '\n';
        }
      });
      
      if (currentSection) {
        sections.push(currentSection);
      }
      
      if (introText.trim()) {
        sections.unshift({ title: 'Summary', content: introText.trim() });
      }
    }
    
    return sections;
  };

  const formatContent = (text) => {
    // Format bullet points
    text = text.replace(/^- (.+)$/gm, '<li class="ml-4 mb-1">$1</li>');
    text = text.replace(/^• (.+)$/gm, '<li class="ml-4 mb-1">$1</li>');
    
    // Wrap consecutive <li> in <ul>
    text = text.replace(/(<li[^>]*>.*<\/li>\s*)+/gs, (match) => `<ul class="list-disc space-y-1 my-2">${match}</ul>`);
    
    // Format bold text (but not already in HTML tags)
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
    
    // Format percentages and currency (USD)
    text = text.replace(/(\$[\d,]+(?:\.\d+)?[MKB]?)/g, '<span class="font-semibold text-green-600">$1</span>');
    text = text.replace(/(\d+(?:\.\d+)?%)/g, '<span class="font-semibold text-purple-600">$1</span>');
    
    // Format line breaks
    text = text.replace(/\n\n/g, '<br/><br/>');
    text = text.replace(/\n/g, '<br/>');
    
    return text;
  };

  const quickActions = i18n.language === 'tr' ? [
    { 
      label: 'Takvim Analizi', 
      query: 'Her iki projenin takvim performansını analiz et. TPI değerlerini karşılaştır, hangi disiplinlerin takvimin gerisinde olduğunu belirle ve takvim kurtarma önerileri sun. Disipline göre planlanan ve gerçekleşen ilerlemeyi gösteren bir grafik ekle.', 
      icon: Clock, 
      gradient: 'from-blue-500 to-cyan-600' 
    },
    { 
      label: 'Maliyet Performansı', 
      query: 'Her iki proje için maliyet sapma analizi yap. Mevcut MPI nedir? Tamamlanma Tahmini (TT) ve Tamamlanma Sapması (TS) hesapla. Hangi maliyet kategorilerinde en yüksek sapma var? Bütçe ve gerçekleşen dağılım grafiği göster.', 
      icon: DollarSign, 
      gradient: 'from-green-500 to-emerald-600' 
    },
    { 
      label: 'Risk Değerlendirmesi', 
      query: 'Her iki projedeki en önemli riskleri belirle ve değerlendir. Takvim gecikmeleri, maliyet aşımları, tedarik gecikmeleri ve kaynak kısıtlamalarını göz önünde bulundur. Her riski olasılık ve etkiye göre derecelendir. Tahmini etkisiyle azaltma stratejileri sun.', 
      icon: AlertCircle, 
      gradient: 'from-red-500 to-pink-600' 
    },
    { 
      label: 'Ne Olursa Senaryoları', 
      query: 'Ne olursa analizi yap: 1) İş-402\'ye 50 işçi daha eklersek ne olur? 2) Tedarik gecikmeleri 2 ay uzarsa ne olur? 3) Takvimi %10 hızlandırmamız gerekirse ne olur? Her senaryo için finansal ve takvim etkisini göster.', 
      icon: Target, 
      gradient: 'from-purple-500 to-violet-600' 
    }
  ] : [
    { 
      label: 'Schedule Analysis', 
      query: 'Analyze the schedule performance of both projects. Compare SPI values, identify which disciplines are behind schedule, and provide recommendations for schedule recovery. Include a chart showing planned vs actual progress by discipline.', 
      icon: Clock, 
      gradient: 'from-blue-500 to-cyan-600' 
    },
    { 
      label: 'Cost Performance', 
      query: 'Perform cost variance analysis for both projects. What is the current CPI? Calculate EAC (Estimate at Completion) and VAC (Variance at Completion). Which cost categories have the highest variance? Show budget vs actual breakdown chart.', 
      icon: DollarSign, 
      gradient: 'from-green-500 to-emerald-600' 
    },
    { 
      label: 'Risk Assessment', 
      query: 'Identify and assess the top risks across both projects. Consider schedule delays, cost overruns, procurement delays, and resource constraints. Rate each risk by probability and impact. Provide mitigation strategies with estimated impact.', 
      icon: AlertCircle, 
      gradient: 'from-red-500 to-pink-600' 
    },
    { 
      label: 'What-If Scenarios', 
      query: 'Run what-if analysis: 1) What if we add 50 more workers to İş-402? 2) What if procurement delays extend by 2 months? 3) What happens if we need to accelerate the schedule by 10%? Show financial and schedule impact for each scenario.', 
      icon: Target, 
      gradient: 'from-purple-500 to-violet-600' 
    }
  ];

  const handleSend = async (query = input) => {
    if (!query.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Call real AI API with language parameter
      const response = await axios.post(`${API}/ai/chat`, {
        message: query,
        session_id: sessionId,
        language: i18n.language  // Pass current language (en or tr)
      });

      // Parse response for charts and tables
      const parsed = parseAIResponse(response.data.response);

      // Add AI response with potential chart/table data
      setMessages(prev => [...prev, {
        type: 'ai',
        content: parsed.content,
        chartData: parsed.chartData,
        chartType: parsed.chartType,
        tableData: parsed.tableData,
        timestamp: new Date()
      }]);

      // Store session ID
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      toast.success(i18n.language === 'tr' ? 'AI analizi tamamlandı' : 'AI analysis complete');
    } catch (error) {
      console.error('AI chat error:', error);
      toast.error(i18n.language === 'tr' ? 'AI yanıtı alınamadı' : 'Failed to get AI response');
      
      // Fallback message with sample chart
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'I apologize for the technical difficulty. Here\'s a sample analysis of your cash flow:',
        chartData: [
          { name: 'Week 1', value: 45000 },
          { name: 'Week 2', value: 52000 },
          { name: 'Week 3', value: 38000 },
          { name: 'Week 4', value: 61000 }
        ],
        chartType: 'bar',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = (query) => {
    handleSend(query);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="agentic-ai-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Brain size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">AI Assistant</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="status-dot active"></span>
              <p className="text-xs text-gray-500">GPT-4o • Real-time Analysis</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <Sparkles className="text-purple-500" size={20} />
            <span className="text-xs text-purple-600 font-semibold mt-1">AI Powered</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-4 py-4 bg-white border-b border-gray-200">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          {i18n.language === 'tr' ? 'Hızlı Analiz' : 'Quick Analysis'}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={() => handleQuickAction(action.query)}
                disabled={isTyping}
                className={`flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br ${action.gradient} text-white transition-all hover:shadow-lg active:scale-95 disabled:opacity-50`}
                data-testid={`quick-action-${index}`}
              >
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <span className="text-sm font-semibold text-left">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-slide-in`}
          >
            <div className={`max-w-[85%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
              {message.type === 'ai' && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Brain size={14} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500">
                    {i18n.language === 'tr' ? 'AI Asistan (GPT-4o)' : 'AI Assistant (GPT-4o)'}
                  </span>
                </div>
              )}
              
              <div className={`rounded-2xl px-4 py-3 ${
                message.type === 'user' 
                  ? 'bg-sky-500 text-white rounded-tr-sm' 
                  : 'bg-white border border-gray-200 rounded-tl-sm shadow-sm'
              }`}>
                {message.type === 'user' ? (
                  <p className="text-sm whitespace-pre-line leading-relaxed">{message.content}</p>
                ) : (
                  <>
                    {(() => {
                      const sections = parseContentSections(message.content);
                      
                      if (sections.length <= 1) {
                        // Simple content, no accordion
                        return (
                          <div 
                            className="text-sm leading-relaxed prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
                          />
                        );
                      }
                      
                      // Multiple sections, use accordion
                      return (
                        <div className="space-y-3">
                          {sections[0] && sections[0].title === 'Summary' && (
                            <div 
                              className="text-sm leading-relaxed mb-4 pb-4 border-b prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: formatContent(sections[0].content) }}
                            />
                          )}
                          
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {sections.filter(s => s.title !== 'Summary').map((section, idx) => (
                              <AccordionItem 
                                key={idx} 
                                value={`section-${idx}`}
                                className="border border-gray-200 rounded-lg px-3 bg-gray-50/50"
                              >
                                <AccordionTrigger className="text-sm font-semibold text-gray-900 hover:no-underline py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                      <span className="text-xs font-bold text-purple-600">{idx + 1}</span>
                                    </div>
                                    <span className="text-left">{section.title}</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-gray-700 pt-2 pb-3">
                                  <div 
                                    className="prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: formatContent(section.content) }}
                                  />
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      );
                    })()}
                  </>
                )}
                
                {/* Render Chart if available */}
                {message.chartData && message.chartType && (
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 size={18} className="text-purple-600" />
                      <span className="text-sm font-semibold text-purple-900">Visual Analysis</span>
                    </div>
                    {renderChart(message.chartData, message.chartType)}
                  </div>
                )}

                {/* Render Table if available */}
                {message.tableData && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-3">
                      <TableIcon size={18} className="text-blue-600" />
                      <span className="text-sm font-semibold text-blue-900">Data Table</span>
                    </div>
                    {renderTable(message.tableData)}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 mt-2">
                {message.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader className="w-4 h-4 text-purple-500 animate-spin" />
                <span className="text-sm text-gray-500">
                  {i18n.language === 'tr' ? 'AI verilerinizi analiz ediyor...' : 'AI is analyzing your data...'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div className="bg-white border-t border-gray-200 px-4 py-4 pb-safe">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSend()}
            placeholder={i18n.language === 'tr' ? 'Projeleriniz hakkında sorun...' : 'Ask about your projects...'}
            className="flex-1"
            disabled={isTyping}
            data-testid="chat-input"
          />
          <Button
            onClick={() => handleSend()}
            disabled={!input.trim() || isTyping}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center disabled:opacity-50"
            data-testid="send-button"
          >
            {isTyping ? <Loader size={20} className="animate-spin" /> : <Send size={20} />}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          {i18n.language === 'tr' 
            ? 'Gerçek proje verileriyle OpenAI GPT-4o tarafından desteklenmektedir' 
            : 'Powered by OpenAI GPT-4o with real project data'}
        </p>
      </div>
    </div>
  );
};

export default AgenticAI;
